import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useList, useGo, useCustomMutation, useCustom, useApiUrl } from '@refinedev/core';
import { useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { FormHeader } from '@/components/forms';
import { Plus, Package, User, Calendar, ChevronDown, ChevronRight, LayoutList, Kanban } from 'lucide-react';
import { OrdersTableView } from './OrdersTableView';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { cn } from '@/lib/utils';
import type { OrderTargetType } from '@masters-club/types';
import { getTargetTypeLabel, type Order, type OrderStatusConfig, type AdminUser } from './types';
import { OrdersFiltersToolbar, type OrdersFilterValues } from './OrdersFiltersToolbar';
import { OrdersFunnelTabs } from './OrdersFunnelTabs';
import { OrdersSearchInput } from './OrdersSearchInput';
import { StatusAnalyticsModal } from './StatusAnalyticsModal';
import { CreateOrderInvoiceDialog } from './components/CreateOrderInvoiceDialog';

const KANBAN_PAGE_SIZE = 20;
const DEFAULTS_STORAGE_KEY = 'orders-kanban-filter-defaults-v1';
/**
 * LocalStorage key for persisting which columns are collapsed per-URL-state.
 * Uses the URL search string as a key suffix so different filter combos can have
 * different collapsed sets (e.g. collapsed "Refund" in one funnel but shown in another).
 */
const COLLAPSED_STORAGE_PREFIX = 'orders-kanban-collapsed-v1:';

// --- Kanban response types ---

interface KanbanStats {
  ordersCount: number;
  byCurrency: Record<string, { paidSum: string; totalSum: string }>;
}

interface KanbanColumnData {
  orders: Order[];
  stats: KanbanStats;
}

interface KanbanResponse {
  statuses: OrderStatusConfig[];
  columns: Record<number, KanbanColumnData>;
  availableCurrencies: string[];
}

interface KanbanColumnPage {
  orders: Order[];
  hasMore: boolean;
}

function getDndOrder(data: Record<string, unknown> | undefined): Order | undefined {
  if (!data || typeof data !== 'object' || !('order' in data)) { return undefined; }
  const candidate = data.order;
  if (
    typeof candidate === 'object'
    && candidate !== null
    && 'id' in candidate
    && 'statusConfigId' in candidate
  ) {
    return candidate as Order;
  }
  return undefined;
}

const currencyFormatter = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function formatCurrency(value: string | number, currency?: string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const formatted = currencyFormatter.format(num);
  return currency ? `${formatted} ${currency}` : formatted;
}

function formatRelativeDate(dateString: string): string {
  const now = Date.now();
  const date = new Date(dateString).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) { return 'только что'; }
  if (diffMin < 60) { return `${diffMin}м назад`; }
  if (diffHours < 24) { return `${diffHours}ч назад`; }
  if (diffDays < 7) { return `${diffDays}д назад`; }

  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

// --- URL / localStorage helpers for filter persistence ---

function parseUrlFilters(params: URLSearchParams): OrdersFilterValues {
  const result: OrdersFilterValues = {};
  const search = params.get('search');
  if (search) { result.search = search; }
  const assignedAdminId = params.get('assignedAdminId');
  if (assignedAdminId) { result.assignedAdminId = Number(assignedAdminId); }
  const targetType = params.get('targetType');
  if (targetType) { result.targetType = targetType as OrderTargetType; }
  const targetId = params.get('targetId');
  if (targetId) { result.targetId = Number(targetId); }
  const pricingTierId = params.get('pricingTierId');
  if (pricingTierId) { result.pricingTierId = Number(pricingTierId); }
  const totalFrom = params.get('totalFrom');
  if (totalFrom) { result.totalFrom = Number(totalFrom); }
  const totalTo = params.get('totalTo');
  if (totalTo) { result.totalTo = Number(totalTo); }
  const createdFrom = params.get('createdFrom');
  if (createdFrom) { result.createdFrom = createdFrom; }
  const createdTo = params.get('createdTo');
  if (createdTo) { result.createdTo = createdTo; }
  const currency = params.get('currency');
  if (currency) { result.currency = currency; }
  return result;
}

function filtersToUrlParams(values: OrdersFilterValues): Record<string, string> {
  const result: Record<string, string> = {};
  if (values.search) { result.search = values.search; }
  if (values.assignedAdminId !== undefined) { result.assignedAdminId = String(values.assignedAdminId); }
  if (values.targetType) { result.targetType = values.targetType; }
  if (values.targetId !== undefined) { result.targetId = String(values.targetId); }
  if (values.pricingTierId !== undefined) { result.pricingTierId = String(values.pricingTierId); }
  if (values.totalFrom !== undefined) { result.totalFrom = String(values.totalFrom); }
  if (values.totalTo !== undefined) { result.totalTo = String(values.totalTo); }
  if (values.createdFrom) { result.createdFrom = values.createdFrom; }
  if (values.createdTo) { result.createdTo = values.createdTo; }
  if (values.currency) { result.currency = values.currency; }
  return result;
}

/**
 * Build backend query params from filter values for /orders/kanban endpoint.
 * Note: backend accepts targetType, targetId, userId, search, createdFrom/To, currency, assignedAdminId.
 * totalFrom/totalTo/pricingTierId are local UI filters (kept in URL, but not passed to /kanban —
 * they operate on list view only).
 */
function filtersToKanbanQuery(values: OrdersFilterValues): Record<string, string> {
  const result: Record<string, string> = {};
  if (values.search) { result.search = values.search; }
  if (values.assignedAdminId !== undefined) { result.assignedAdminId = String(values.assignedAdminId); }
  if (values.targetType) { result.targetType = values.targetType; }
  if (values.targetId !== undefined) { result.targetId = String(values.targetId); }
  if (values.createdFrom) { result.createdFrom = values.createdFrom; }
  if (values.createdTo) { result.createdTo = values.createdTo; }
  if (values.currency) { result.currency = values.currency; }
  return result;
}

function loadDefaults(): OrdersFilterValues | null {
  try {
    const raw = localStorage.getItem(DEFAULTS_STORAGE_KEY);
    if (!raw) { return null; }
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) { return null; }
    return parsed as OrdersFilterValues;
  } catch {
    return null;
  }
}

function saveDefaults(values: OrdersFilterValues): void {
  try {
    localStorage.setItem(DEFAULTS_STORAGE_KEY, JSON.stringify(values));
  } catch {
    // ignore
  }
}

// --- Collapsed columns per-URL persistence ---

/**
 * Collapsed-state key is scoped by filter "context" (currency + targetType),
 * so different funnels / currencies can remember different sets of hidden statuses.
 */
function getCollapsedStorageKey(values: OrdersFilterValues): string {
  const context = {
    currency: values.currency ?? '',
    targetType: values.targetType ?? '',
  };
  return `${COLLAPSED_STORAGE_PREFIX}${JSON.stringify(context)}`;
}

function loadCollapsed(values: OrdersFilterValues): Set<number> {
  try {
    const raw = localStorage.getItem(getCollapsedStorageKey(values));
    if (!raw) { return new Set(); }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) { return new Set(); }
    return new Set(parsed.filter((n): n is number => typeof n === 'number'));
  } catch {
    return new Set();
  }
}

function saveCollapsed(values: OrdersFilterValues, ids: Set<number>): void {
  try {
    localStorage.setItem(getCollapsedStorageKey(values), JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

// --- Status form type — shared with StatusAnalyticsModal ---

interface StatusFormData {
  name: string;
  color: string;
  description: string;
  isInitial: boolean;
  isFinal: boolean;
}

// --- Payment Status Badge ---

interface PaymentStatusProps {
  paidAmount: string;
  total: string;
  currency: string;
}

function PaymentStatus({ paidAmount, total, currency }: PaymentStatusProps) {
  const paid = parseFloat(paidAmount);
  const orderTotal = parseFloat(total);

  if (orderTotal <= 0) {
    return null;
  }

  if (paid >= orderTotal) {
    return (
      <span className="text-[11px] font-medium text-emerald-600">
        Оплачено полностью
      </span>
    );
  }

  if (paid > 0) {
    return (
      <span className="text-[11px] font-medium text-amber-600">
        Оплачено: {formatCurrency(paidAmount, currency)} / {formatCurrency(total, currency)}
      </span>
    );
  }

  return (
    <span className="text-[11px] font-medium text-red-500">
      Не оплачено
    </span>
  );
}

// --- Draggable Order Card ---

interface OrderCardProps {
  order: Order;
  onClick: () => void;
  adminMap: Map<number, string>;
  isDragOverlay?: boolean;
}

function OrderCard({ order, onClick, adminMap, isDragOverlay }: OrderCardProps) {
  const userName = [order.user?.firstName, order.user?.lastName].filter(Boolean).join(' ')
    || order.user?.username
    || `#${order.userId}`;
  const items = order.items ?? [];
  const assignedAdmin = order.assignedAdminId ? adminMap.get(order.assignedAdminId) : null;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `order-${order.id}`,
    data: { order },
    disabled: isDragOverlay,
  });

  return (
    <Card
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        'cursor-grab active:cursor-grabbing py-2.5 px-3 gap-1.5 hover:shadow-md transition-shadow select-none touch-none',
        isDragging && 'opacity-30',
        isDragOverlay && 'shadow-xl ring-2 ring-primary/30 rotate-2',
      )}
      onClick={(e) => {
        if (isDragOverlay) { return; }
        e.stopPropagation();
        onClick();
      }}
    >
      {/* Row 1: ID + date */}
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-muted-foreground">#{order.id}</span>
        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatRelativeDate(order.createdAt)}
        </span>
      </div>

      {/* Row 2: User */}
      <div className="flex items-center gap-1.5">
        <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        <p className="text-sm font-medium truncate">{userName}</p>
      </div>

      {/* Row 3: Items list — shows product + tier (if selected) */}
      {items.length > 0 && (
        <div className="space-y-0.5">
          {items.slice(0, 2).map((item) => (
            <div key={item.id} className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-[10px] px-1 py-0 font-normal">
                  {getTargetTypeLabel(item.targetType)}
                </Badge>
                <span className="truncate">{item.target?.name ?? `#${item.targetId}`}</span>
              </div>
              {item.pricingTierInfo?.name && (
                <div className="flex items-center gap-1 pl-1">
                  <span className="text-[10px] text-muted-foreground/70">Тариф:</span>
                  <span className="text-[10px] font-medium text-foreground/80 truncate">
                    {item.pricingTierInfo.name}
                  </span>
                </div>
              )}
            </div>
          ))}
          {items.length > 2 && (
            <span className="text-[11px] text-muted-foreground">+ ещё {items.length - 2}</span>
          )}
        </div>
      )}

      {/* Row 4: Total + payment status */}
      <div className="flex flex-col gap-1 pt-1 border-t border-border/50">
        <div className="flex items-center justify-between">
          <div className="font-mono text-sm">
            <span className="font-semibold">{formatCurrency(order.total, order.currency)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {assignedAdmin && (
              <span className="text-[11px] text-muted-foreground truncate max-w-[80px]">{assignedAdmin}</span>
            )}
            {items.length > 0 && (
              <Badge variant="secondary" className="text-[10px] gap-0.5 px-1 py-0">
                <Package className="h-2.5 w-2.5" />
                {items.length}
              </Badge>
            )}
          </div>
        </div>
        <PaymentStatus paidAmount={order.paidAmount} total={order.total} currency={order.currency} />
      </div>
    </Card>
  );
}

// --- Droppable Kanban Column with scroll-load ---

interface KanbanColumnProps {
  status: OrderStatusConfig;
  initialData: KanbanColumnData;
  kanbanQuery: Record<string, string>;
  onOrderClick: (order: Order) => void;
  onHeaderClick: () => void;
  adminMap: Map<number, string>;
  isOver: boolean;
  apiUrl: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

function KanbanColumn({
  status,
  initialData,
  kanbanQuery,
  onOrderClick,
  onHeaderClick,
  adminMap,
  isOver,
  apiUrl,
  isCollapsed,
  onToggleCollapse,
}: KanbanColumnProps) {
  const { setNodeRef } = useDroppable({
    id: `status-${status.id}`,
    data: { statusId: status.id },
  });

  // Infinite query: start with initialData as first page, load more on scroll.
  const infiniteQuery = useInfiniteQuery<KanbanColumnPage>({
    queryKey: ['kanban-column', status.id, kanbanQuery],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = pageParam as number;
      // First page is already provided via initialData — skip network call.
      if (offset === 0) {
        return { orders: initialData.orders, hasMore: initialData.orders.length >= KANBAN_PAGE_SIZE };
      }
      const params = new URLSearchParams(kanbanQuery);
      params.set('offset', String(offset));
      params.set('limit', String(KANBAN_PAGE_SIZE));
      const response = await fetch(`${apiUrl}/orders/kanban/columns/${status.id}?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to load column ${status.id} page`);
      }
      const body = await response.json() as { data: KanbanColumnPage };
      return body.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) { return; }
      const loaded = allPages.reduce((sum, p) => sum + p.orders.length, 0);
      return loaded;
    },
    initialData: {
      pages: [{ orders: initialData.orders, hasMore: initialData.orders.length >= KANBAN_PAGE_SIZE }],
      pageParams: [0],
    },
    staleTime: 30_000,
    enabled: !isCollapsed,
  });

  const allOrders = useMemo(() => {
    const pages = infiniteQuery.data?.pages ?? [];
    return pages.flatMap((p) => p.orders);
  }, [infiniteQuery.data]);

  // Intersection observer for scroll-load
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) { return; }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
            void infiniteQuery.fetchNextPage();
          }
        }
      },
      { root: null, rootMargin: '100px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [infiniteQuery]);

  const { stats } = initialData;
  const currencies = Object.keys(stats.byCurrency);
  const hasAggregates = stats.ordersCount > 0 && currencies.length > 0;

  // --- Collapsed mode: narrow vertical rail ---
  if (isCollapsed) {
    return (
      <div
        ref={setNodeRef}
        className={cn(
          'flex flex-col min-w-[44px] w-[44px] flex-shrink-0 h-full rounded-xl bg-muted/40 transition-colors',
          isOver && 'bg-primary/10 ring-2 ring-primary/30 ring-dashed',
        )}
      >
        <button
          className="flex flex-col items-center gap-2 py-3 w-full hover:bg-muted rounded-xl transition-colors"
          onClick={onToggleCollapse}
          title={`Развернуть «${status.name}»`}
        >
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
          <div
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: status.color }}
          />
          <span
            className="text-xs font-medium [writing-mode:vertical-rl] rotate-180 whitespace-nowrap max-h-[200px] overflow-hidden text-ellipsis"
          >
            {status.name}
          </span>
          <Badge variant="secondary" className="text-[10px] px-1 py-0 font-mono">
            {stats.ordersCount}
          </Badge>
        </button>
      </div>
    );
  }

  // --- Expanded mode ---
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col min-w-[280px] w-[280px] flex-shrink-0 h-full rounded-xl transition-colors',
        isOver && 'bg-primary/5 ring-2 ring-primary/20 ring-dashed',
      )}
    >
      {/* Column header with aggregates + collapse toggle — single seamless group */}
      <div className="flex items-stretch mb-2 rounded-lg overflow-hidden bg-muted/50">
        <button
          className="flex flex-col gap-1.5 px-3 py-2.5 hover:bg-muted/80 transition-colors text-left flex-1 min-w-0"
          onClick={onHeaderClick}
        >
          <div className="flex items-center gap-2 w-full">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: status.color }}
            />
            <span className="font-medium text-sm truncate flex-1">{status.name}</span>
            <Badge variant="secondary" className="text-xs px-1.5 py-0 font-mono">
              {stats.ordersCount}
            </Badge>
          </div>
          {hasAggregates && (
            <div className="flex flex-col gap-0.5 pl-5 w-full">
              {currencies.map((cur) => {
                const { paidSum, totalSum } = stats.byCurrency[cur];
                return (
                  <div key={cur} className="flex items-center text-[11px] font-mono gap-1 truncate">
                    <span className="text-emerald-600 font-medium">
                      {formatCurrency(paidSum, cur)}
                    </span>
                    <span className="text-muted-foreground">
                      / {formatCurrency(totalSum, cur)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </button>
        <button
          type="button"
          className="flex items-center justify-center w-8 hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors border-l border-border/40"
          onClick={onToggleCollapse}
          title="Свернуть колонку"
          aria-label="Свернуть колонку"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {/* Orders list */}
      <div className="flex flex-col gap-1.5 flex-1 px-0.5 overflow-y-auto">
        {allOrders.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            onClick={() => onOrderClick(order)}
            adminMap={adminMap}
          />
        ))}

        {allOrders.length === 0 && (
          <div className={cn(
            'text-center py-8 text-sm text-muted-foreground rounded-lg border-2 border-dashed border-transparent transition-colors',
            isOver && 'border-primary/30 bg-primary/5',
          )}>
            {isOver ? 'Перетащите сюда' : 'Нет заказов'}
          </div>
        )}

        {/* Sentinel for scroll-load */}
        {infiniteQuery.hasNextPage && (
          <div ref={sentinelRef} className="py-2 text-center text-xs text-muted-foreground">
            {infiniteQuery.isFetchingNextPage ? 'Загрузка...' : '•'}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Kanban Column Skeleton ---

function KanbanColumnSkeleton() {
  return (
    <div className="flex flex-col min-w-[280px] w-[280px] flex-shrink-0">
      <Skeleton className="h-10 w-full rounded-lg mb-2" />
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-[120px] w-full rounded-xl" />
        <Skeleton className="h-[120px] w-full rounded-xl" />
        <Skeleton className="h-[120px] w-full rounded-xl" />
      </div>
    </div>
  );
}

// --- Main Component ---

export function OrdersList() {
  useScrollRestoration();
  const go = useGo();
  const queryClient = useQueryClient();
  const apiUrl = useApiUrl();
  const [searchParams, setSearchParams] = useSearchParams();

  // View mode: kanban or table
  const [viewMode, setViewMode] = usePersistedState<'kanban' | 'table'>('orders:viewMode', 'kanban');

  // Status dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<OrderStatusConfig | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // DnD state
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);

  // Filter values: initialized from URL, falling back to localStorage defaults.
  // We only read URL/defaults once on first render.
  const [filterValues, setFilterValues] = useState<OrdersFilterValues>(() => {
    const urlFilters = parseUrlFilters(searchParams);
    if (Object.keys(urlFilters).length > 0) {
      return urlFilters;
    }
    return loadDefaults() ?? {};
  });

  // Keep URL in sync when filters change (replace, not push — avoid bloating history)
  useEffect(() => {
    const next = filtersToUrlParams(filterValues);
    const current: Record<string, string> = {};
    for (const [k, v] of searchParams.entries()) { current[k] = v; }
    // Skip update if identical
    if (JSON.stringify(next) === JSON.stringify(current)) { return; }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  // Collapsed columns state — scoped by filter context so different funnels remember differently
  const [collapsedColumns, setCollapsedColumns] = useState<Set<number>>(
    () => loadCollapsed(filterValues),
  );

  // Reload collapsed state when filter context (currency / targetType) changes
  useEffect(() => {
    setCollapsedColumns(loadCollapsed(filterValues));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues.currency, filterValues.targetType]);

  const toggleColumnCollapse = useCallback((statusId: number) => {
    setCollapsedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(statusId)) { next.delete(statusId); } else { next.add(statusId); }
      saveCollapsed(filterValues, next);
      return next;
    });
  }, [filterValues]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Kanban query params for backend
  const kanbanQuery = useMemo(() => filtersToKanbanQuery(filterValues), [filterValues]);

  // Kanban data (single request: statuses + stats + first page of orders per column)
  const { result: kanbanResult, query: kanbanQueryState } = useCustom<KanbanResponse>({
    url: `${apiUrl}/orders/kanban`,
    method: 'get',
    config: { query: kanbanQuery },
  });
  const kanbanData = kanbanResult?.data;
  const isLoadingKanban = kanbanQueryState?.isLoading ?? false;
  const refetchKanban = kanbanQueryState?.refetch;

  // Load admins for display
  const adminsResult = useList<AdminUser>({
    resource: 'admin-users',
    pagination: { mode: 'off' },
  });
  const adminsData = adminsResult.result?.data;
  const adminMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const admin of adminsData ?? []) {
      map.set(admin.id, admin.username);
    }
    return map;
  }, [adminsData]);

  // Sort statuses by position
  const sortedStatuses = useMemo(() => {
    const raw = kanbanData?.statuses ?? [];
    return [...raw].sort((a, b) => a.position - b.position);
  }, [kanbanData?.statuses]);

  const availableCurrencies = kanbanData?.availableCurrencies ?? [];

  // Mutations
  const createMutation = useCustomMutation();
  const updateMutation = useCustomMutation();
  const deleteMutation = useCustomMutation();
  const changeStatusMutation = useCustomMutation();

  const isSavingStatus = createMutation.mutation.isPending || updateMutation.mutation.isPending;
  const isDeletingStatus = deleteMutation.mutation.isPending;

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['data', 'default', 'order-statuses'] });
    queryClient.invalidateQueries({ queryKey: ['data', 'default', 'orders'] });
    // Invalidate kanban main endpoint
    if (refetchKanban) { void refetchKanban(); }
    // Invalidate all column infinite queries
    queryClient.invalidateQueries({ queryKey: ['kanban-column'] });
  }, [queryClient, refetchKanban]);

  // DnD handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const order = getDndOrder(event.active.data.current);
    if (order) {
      setActiveOrder(order);
    }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverColumnId(event.over?.id?.toString() ?? null);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveOrder(null);
    setOverColumnId(null);

    const { active, over } = event;
    if (!over) { return; }

    const order = getDndOrder(active.data.current);
    if (!order) { return; }

    const overId = over.id.toString();
    const targetStatusId = overId.startsWith('status-') ? parseInt(overId.replace('status-', ''), 10) : null;
    if (!targetStatusId || targetStatusId === order.statusConfigId) { return; }

    changeStatusMutation.mutate(
      {
        url: `/orders/${order.id}/crm-status`,
        method: 'patch',
        values: { statusId: targetStatusId },
      },
      { onSuccess: invalidateAll },
    );
  }, [changeStatusMutation, invalidateAll]);

  const handleDragCancel = useCallback(() => {
    setActiveOrder(null);
    setOverColumnId(null);
  }, []);

  // Status CRUD handlers
  const handleSaveStatus = useCallback((data: StatusFormData) => {
    const payload = {
      name: data.name,
      description: data.description || undefined,
      color: data.color,
      isInitial: data.isInitial,
      isFinal: data.isFinal,
    };

    if (editingStatus) {
      const isSystem = editingStatus.isSystem;
      const editPayload = isSystem
        ? { name: data.name, description: data.description || undefined, color: data.color }
        : { ...payload, position: editingStatus.position };

      updateMutation.mutate(
        { url: `/order-statuses/${editingStatus.id}`, method: 'put', values: editPayload },
        {
          onSuccess: () => {
            setStatusDialogOpen(false);
            setEditingStatus(null);
            invalidateAll();
          },
        },
      );
    } else {
      const createPayload = { ...payload, position: sortedStatuses.length };
      createMutation.mutate(
        { url: '/order-statuses', method: 'post', values: createPayload },
        {
          onSuccess: () => {
            setStatusDialogOpen(false);
            invalidateAll();
          },
        },
      );
    }
  }, [editingStatus, sortedStatuses.length, updateMutation, createMutation, invalidateAll]);

  const handleDeleteStatus = useCallback(() => {
    if (!editingStatus) { return; }
    setDeleteConfirmOpen(true);
  }, [editingStatus]);

  const confirmDeleteStatus = useCallback(() => {
    if (!editingStatus) { return; }
    deleteMutation.mutate(
      { url: `/order-statuses/${editingStatus.id}`, method: 'delete', values: {} },
      {
        onSuccess: () => {
          setDeleteConfirmOpen(false);
          setStatusDialogOpen(false);
          setEditingStatus(null);
          invalidateAll();
        },
      },
    );
  }, [editingStatus, deleteMutation, invalidateAll]);

  const openCreateStatusDialog = useCallback(() => {
    setEditingStatus(null);
    setStatusDialogOpen(true);
  }, []);

  const openEditStatusDialog = useCallback((status: OrderStatusConfig) => {
    setEditingStatus(status);
    setStatusDialogOpen(true);
  }, []);

  const handleOrderClick = useCallback((order: Order) => {
    go({ to: `/orders/show/${order.id}` });
  }, [go]);

  const handleSaveAsDefaults = useCallback(() => {
    saveDefaults(filterValues);
  }, [filterValues]);

  const isLoading = isLoadingKanban;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <FormHeader
        title="Заказы"
        description="Управление заказами"
        sticky
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  {viewMode === 'kanban' ? (
                    <Kanban className="h-4 w-4" />
                  ) : (
                    <LayoutList className="h-4 w-4" />
                  )}
                  Отображение
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuRadioGroup
                  value={viewMode}
                  onValueChange={(v) => setViewMode(v === 'table' ? 'table' : 'kanban')}
                >
                  <DropdownMenuRadioItem value="kanban">
                    <Kanban className="h-4 w-4 mr-2" />
                    Канбан
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="table">
                    <LayoutList className="h-4 w-4 mr-2" />
                    Таблица
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  Создать
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => setCreateOrderOpen(true)}>
                  <Package className="h-4 w-4 mr-2" />
                  Заказ
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={openCreateStatusDialog}
                  disabled={viewMode !== 'kanban'}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Статус
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <OrdersFunnelTabs
              targetType={filterValues.targetType}
              targetId={filterValues.targetId}
              onChange={({ targetType: nextType, targetId: nextId }) => setFilterValues((prev) => ({
                ...prev,
                targetType: nextType,
                targetId: nextId,
                // Reset pricing tier if targetType changed
                pricingTierId: prev.targetType === nextType ? prev.pricingTierId : undefined,
              }))}
            />
            <OrdersSearchInput
              value={filterValues.search}
              onChange={(next) => setFilterValues((prev) => ({ ...prev, search: next }))}
            />
          </div>
        }
      />

      {/* Additional filters toolbar (admin, product, tier, amount, date, currency) */}
      <div className="mt-4">
        <OrdersFiltersToolbar
          values={filterValues}
          onChange={setFilterValues}
          admins={adminsData ?? []}
          availableCurrencies={availableCurrencies}
          onSaveAsDefaults={handleSaveAsDefaults}
        />
      </div>

      {/* Table view */}
      {viewMode === 'table' && (
        <div className="mt-4 flex-1 overflow-auto">
          <OrdersTableView
            filterValues={filterValues}
            admins={adminsData ?? []}
            statuses={sortedStatuses}
          />
        </div>
      )}

      {/* Kanban board with DnD */}
      {viewMode === 'kanban' && <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <ScrollArea className="w-full flex-1 mt-4">
          <div className={cn('flex gap-4 pb-4 h-full', isLoading && 'min-h-[400px]')}>
            {isLoading ? (
              <>
                <KanbanColumnSkeleton />
                <KanbanColumnSkeleton />
                <KanbanColumnSkeleton />
                <KanbanColumnSkeleton />
              </>
            ) : (sortedStatuses.length === 0 ? (
              <div className="flex-1 flex items-center justify-center py-16 text-muted-foreground">
                <div className="text-center space-y-2">
                  <p>Нет статусов заказов</p>
                  <Button variant="outline" onClick={openCreateStatusDialog}>
                    <Plus className="h-4 w-4 mr-1" />
                    Создать первый статус
                  </Button>
                </div>
              </div>
            ) : (
              sortedStatuses.map((status) => {
                const columnData = kanbanData?.columns?.[status.id] ?? {
                  orders: [],
                  stats: { ordersCount: 0, byCurrency: {} },
                };
                return (
                  <KanbanColumn
                    key={status.id}
                    status={status}
                    initialData={columnData}
                    kanbanQuery={kanbanQuery}
                    onOrderClick={handleOrderClick}
                    onHeaderClick={() => openEditStatusDialog(status)}
                    adminMap={adminMap}
                    isOver={overColumnId === `status-${status.id}`}
                    apiUrl={apiUrl}
                    isCollapsed={collapsedColumns.has(status.id)}
                    onToggleCollapse={() => toggleColumnCollapse(status.id)}
                  />
                );
              })
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Drag overlay */}
        <DragOverlay>
          {activeOrder && (
            <div className="w-[280px]">
              <OrderCard
                order={activeOrder}
                onClick={() => {}}
                adminMap={adminMap}
                isDragOverlay
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>}

      {/* Status analytics + edit modal */}
      <StatusAnalyticsModal
        open={statusDialogOpen}
        onOpenChange={(open) => {
          setStatusDialogOpen(open);
          if (!open) { setEditingStatus(null); }
        }}
        status={editingStatus}
        kanbanQuery={kanbanQuery}
        onSaveStatus={handleSaveStatus}
        onDeleteStatus={handleDeleteStatus}
        onAdvanced={editingStatus ? () => {
          setStatusDialogOpen(false);
          go({ to: `/orders/statuses/edit/${editingStatus.id}` });
        } : undefined}
        isSavingStatus={isSavingStatus}
      />

      {/* Создание заказа пользователю + генерация инвойс-ссылки */}
      <CreateOrderInvoiceDialog
        open={createOrderOpen}
        onOpenChange={setCreateOrderOpen}
        onCreated={invalidateAll}
      />

      {/* Delete confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить статус?</AlertDialogTitle>
            <AlertDialogDescription>
              Статус &quot;{editingStatus?.name}&quot; будет удалён. Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteStatus} disabled={isDeletingStatus}>
              {isDeletingStatus ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
